import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import Text from '@/storybook/components/Text/Text';
import Button from '@/storybook/components/Button/Button';
import Alert from '@/storybook/components/Alert/Alert';
import LoadingSpinner from '@/storybook/components/LoadingSpinner/LoadingSpinner';
import EmptyBoxIcon from '@/storybook/icons/EmptyBoxIcon';
import { CHAT_HISTORY_TEST_IDS } from './testIds';
import styles from './ChatHistoryPanel.module.css';

interface ChatHistoryStatusProps {
  isLoading: boolean;
  isError: boolean;
  loadErrorMessage: string;
  openError: boolean;
  showEmptyState: boolean;
  emptyTitle: string;
  emptySubtitle: string;
  onRetryLoad: () => void;
  onRetryOpen: () => void;
  onDismissLoad: () => void;
  onDismissOpen: () => void;
}

const ChatHistoryStatus: FC<ChatHistoryStatusProps> = ({
  isLoading,
  isError,
  loadErrorMessage,
  openError,
  showEmptyState,
  emptyTitle,
  emptySubtitle,
  onRetryLoad,
  onRetryOpen,
  onDismissLoad,
  onDismissOpen,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={styles.loadingState} data-testid={CHAT_HISTORY_TEST_IDS.loading}>
        <LoadingSpinner hideLoadingMessage size={44} className={styles.spinner} />
        <Text as="p" className={styles.loadingText}>
          {t('components.aiChatDrawer.history.loading')}
        </Text>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        variant="error"
        className={styles.statusAlert}
        message={loadErrorMessage}
        dismissible
        onClose={onDismissLoad}
        action={
          <Button variant="text" size="S" onClick={onRetryLoad} data-testid={CHAT_HISTORY_TEST_IDS.loadErrorRetry}>
            {t('components.aiChatDrawer.history.loadErrorRetry')}
          </Button>
        }
        data-testid={CHAT_HISTORY_TEST_IDS.loadError}
      />
    );
  }

  if (openError) {
    return (
      <Alert
        variant="error"
        className={styles.statusAlert}
        message={t('components.aiChatDrawer.history.openError')}
        dismissible
        onClose={onDismissOpen}
        action={
          <Button variant="text" size="S" onClick={onRetryOpen} data-testid={CHAT_HISTORY_TEST_IDS.openErrorRetry}>
            {t('components.aiChatDrawer.history.openErrorRetry')}
          </Button>
        }
        data-testid={CHAT_HISTORY_TEST_IDS.openError}
      />
    );
  }

  if (showEmptyState) {
    return (
      <div className={styles.emptyState} data-testid={CHAT_HISTORY_TEST_IDS.empty}>
        <EmptyBoxIcon className={styles.emptyIllustration} aria-hidden="true" />
        <div className={styles.emptyDescription}>
          <Text as="p" className={styles.emptyTitle}>
            {emptyTitle}
          </Text>
          <Text as="p" className={styles.emptySubtitle}>
            {emptySubtitle}
          </Text>
        </div>
      </div>
    );
  }

  return null;
};

export default ChatHistoryStatus;
